import { useState } from 'react';
import { AlertTriangle, Check, Eye, SlidersHorizontal, X } from 'lucide-react';

import type {
    BulkSimulateRow, DaysSync, EngineVersion, LinearEngineParams, Thermometer, TypeSource,
} from '@/types/price-structure';

import {
    AGGRESSIVENESS, DAYS_SYNC, DEFAULT_DIALS, DEFAULT_LINEAR_PARAMS, ENGINES, GRANULARITY,
    HEALTH_LABEL, HEALTH_STYLE, THERMOMETERS, TYPE_SOURCE, aggressivenessDetail, evaluateHealth,
    pmrTargetOf,
} from '../constants';
import { EngineTuningModal } from './EngineTuningModal';

type Dials = typeof DEFAULT_DIALS;

interface DomainSetupCardProps {
    domainName: string;
    dials: Dials;
    existingSetupId: number | null;
    result?: BulkSimulateRow;
    onChange: (patch: Partial<Dials>) => void;
    onChangeLinear: (patch: Partial<LinearEngineParams>) => void;
    onInspect: () => void;
}

/**
 * Um card por domínio, no lugar da linha de tabela: os dials do v4 são muitos
 * para caberem numa linha sem virar sopa de campos sem rótulo.
 */
export function DomainSetupCard({
    domainName, dials, existingSetupId, result, onChange, onChangeLinear, onInspect,
}: DomainSetupCardProps) {
    const [tuning, setTuning] = useState(false);
    const isV4 = dials.engine_version === 'v4';
    const linear: LinearEngineParams = { ...DEFAULT_LINEAR_PARAMS, ...(dials.engine_params ?? {}) };

    return (
        <div className="rounded-xl border border-gray-200 bg-white">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
                <div className="min-w-0">
                    <div className="truncate font-medium text-gray-900">{domainName}</div>
                    {existingSetupId && (
                        <span className="mt-0.5 inline-flex items-center gap-1 text-xs text-amber-700">
                            <AlertTriangle className="h-3 w-3" />
                            já tem setup — será atualizado
                        </span>
                    )}
                </div>

                <div className="flex items-center gap-1 rounded-lg bg-gray-100 p-0.5">
                    {ENGINES.map((option) => (
                        <button
                            key={option.value}
                            type="button"
                            title={option.detail}
                            onClick={() => onChange({ engine_version: option.value as EngineVersion })}
                            className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                                dials.engine_version === option.value
                                    ? 'bg-white text-gray-900 shadow-sm'
                                    : 'text-gray-500 hover:text-gray-800'
                            }`}
                        >
                            {option.short}
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid gap-4 p-4 lg:grid-cols-[1fr_auto]">
                <div className="space-y-4">
                    <div className="grid gap-3 sm:grid-cols-4">
                        <Field label="Segmentação">
                            <select
                                value={dials.type_source}
                                onChange={(e) => onChange({ type_source: e.target.value as TypeSource })}
                                className={control}
                            >
                                {TYPE_SOURCE.map((t) => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                        </Field>

                        {isV4 ? (
                            <Field
                                label="Receita mínima"
                                hint="Receita da combinação na janela para ela virar regra própria. Abaixo disso, usa a do país ou do bloco."
                            >
                                <div className="flex items-center gap-1.5">
                                    <span className="text-sm text-gray-400">US$</span>
                                    <input
                                        type="number"
                                        step={0.5}
                                        min={0}
                                        value={linear.min_revenue}
                                        onChange={(e) =>
                                            onChangeLinear({ min_revenue: Number(e.target.value) || 0 })
                                        }
                                        className={control}
                                    />
                                </div>
                            </Field>
                        ) : (
                            <Field label="Mín. impressões" hint="Filtro duro de massa mínima por segmento.">
                                <input
                                    type="number"
                                    min={0}
                                    value={dials.min_impressions}
                                    onChange={(e) => onChange({ min_impressions: Number(e.target.value) || 0 })}
                                    className={control}
                                />
                            </Field>
                        )}

                        <Field
                            label="Janela"
                            hint={isV4
                                ? 'Janela do eCPM que serve de semente e de limite. O v4 roda todo dia de qualquer forma.'
                                : 'Janela de análise e cadência da execução.'}
                        >
                            <select
                                value={dials.days_sync}
                                onChange={(e) => onChange({ days_sync: Number(e.target.value) as DaysSync })}
                                className={control}
                            >
                                {DAYS_SYNC.map((d) => (
                                    <option key={d.value} value={d.value}>
                                        {isV4 ? d.short : d.label}
                                    </option>
                                ))}
                            </select>
                        </Field>

                        <Field
                            label={isV4 ? 'Degraus' : 'Steps pedidos'}
                            hint={isV4 ? 'Degraus da abertura até o chão, sem contar o 0,05.' : undefined}
                        >
                            <input
                                type="number"
                                min={4}
                                max={40}
                                value={dials.steps_count}
                                onChange={(e) => onChange({ steps_count: Number(e.target.value) || 6 })}
                                className={control}
                            />
                        </Field>
                    </div>

                    {isV4 ? (
                        <div className="grid gap-3 sm:grid-cols-2">
                            <Panel title="Cascata" tone="sky">
                                <Inline label="abre em eCPM ×">
                                    <input
                                        type="number" step={0.05} min={1} max={5}
                                        value={linear.open_multiplier}
                                        onChange={(e) =>
                                            onChangeLinear({ open_multiplier: Number(e.target.value) })}
                                        className={mini}
                                    />
                                </Inline>
                                <Inline label="chão">
                                    <input
                                        type="number" step={1} min={1} max={99}
                                        value={Math.round(linear.floor_fraction * 100)}
                                        onChange={(e) =>
                                            onChangeLinear({ floor_fraction: Number(e.target.value) / 100 })}
                                        className={mini}
                                    />
                                    <span className="text-xs text-gray-500">% da abertura</span>
                                </Inline>
                                <label className="flex items-center gap-2 text-xs text-gray-600">
                                    <input
                                        type="checkbox"
                                        checked={linear.fallback}
                                        onChange={(e) => onChangeLinear({ fallback: e.target.checked })}
                                        className="h-3.5 w-3.5 rounded border-gray-300"
                                    />
                                    tentar US$ 0,05 no fim
                                </label>
                            </Panel>

                            <Panel title="Otimizador diário" tone="emerald">
                                <Inline label="corrige pelo">
                                    <select
                                        value={linear.thermometer}
                                        onChange={(e) =>
                                            onChangeLinear({ thermometer: e.target.value as Thermometer })}
                                        title={THERMOMETERS.find((t) => t.value === linear.thermometer)?.detail}
                                        className={`${mini} w-auto`}
                                    >
                                        {THERMOMETERS.map((t) => (
                                            <option key={t.value} value={t.value}>{t.label}</option>
                                        ))}
                                    </select>
                                </Inline>
                                <Inline label="alvo">
                                    <input
                                        type="number" step={1} min={1} max={100}
                                        value={linear.target}
                                        onChange={(e) => onChangeLinear({ target: Number(e.target.value) })}
                                        className={mini}
                                    />
                                    <span className="text-xs text-gray-500">% de preenchimento</span>
                                </Inline>
                                <button
                                    type="button"
                                    onClick={() => setTuning(true)}
                                    className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 hover:underline"
                                >
                                    <SlidersHorizontal className="h-3.5 w-3.5" />
                                    velocidade, limites e zona morta
                                </button>
                            </Panel>
                        </div>
                    ) : (
                        <div className="grid gap-3 sm:grid-cols-3">
                            <Field label="Agressividade">
                                <select
                                    value={dials.aggressiveness}
                                    onChange={(e) => onChange({ aggressiveness: Number(e.target.value) })}
                                    title={aggressivenessDetail(
                                        AGGRESSIVENESS.find((a) => a.value === dials.aggressiveness)!,
                                    )}
                                    className={control}
                                >
                                    {AGGRESSIVENESS.map((a) => (
                                        <option key={a.value} value={a.value}>{a.label}</option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="Granularidade">
                                <select
                                    value={dials.granularity}
                                    onChange={(e) => onChange({ granularity: Number(e.target.value) })}
                                    className={control}
                                >
                                    {GRANULARITY.map((g) => (
                                        <option key={g.value} value={g.value}>{g.label}</option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="PMR alvo" hint="Em branco segue o alvo da agressividade.">
                                <input
                                    type="number" min={1} max={100}
                                    value={dials.pmr_target_override ?? pmrTargetOf(dials.aggressiveness)}
                                    onChange={(e) => {
                                        const value = Number(e.target.value);
                                        onChange({
                                            pmr_target_override:
                                                !value || value === pmrTargetOf(dials.aggressiveness)
                                                    ? null
                                                    : value,
                                        });
                                    }}
                                    className={`${control} ${
                                        dials.pmr_target_override !== null
                                            ? 'border-blue-300 bg-blue-50/60 font-medium text-blue-900'
                                            : ''
                                    }`}
                                />
                            </Field>
                        </div>
                    )}
                </div>

                <SimulationPanel result={result} onInspect={onInspect} />
            </div>

            <EngineTuningModal
                open={tuning}
                onOpenChange={setTuning}
                domainName={domainName}
                params={linear}
                onApply={onChangeLinear}
            />
        </div>
    );
}

const control =
    'h-9 w-full rounded-lg border border-gray-200 bg-white px-2.5 text-sm text-gray-800 ' +
    'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';

const mini =
    'h-8 w-20 rounded-lg border border-gray-200 bg-white px-2 text-sm font-medium text-gray-900 ' +
    'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';

function Field({ label, hint, children }: {
    label: string;
    hint?: string;
    children: React.ReactNode;
}) {
    return (
        <label className="block" title={hint}>
            <span className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                {label}
            </span>
            <div className="mt-1">{children}</div>
        </label>
    );
}

function Panel({ title, tone, children }: {
    title: string;
    tone: 'sky' | 'emerald';
    children: React.ReactNode;
}) {
    const tones = {
        sky: 'border-sky-100 bg-sky-50/50 text-sky-900',
        emerald: 'border-emerald-100 bg-emerald-50/50 text-emerald-900',
    };
    return (
        <div className={`space-y-2 rounded-lg border p-3 ${tones[tone]}`}>
            <div className="text-[11px] font-semibold uppercase tracking-wide">{title}</div>
            {children}
        </div>
    );
}

function Inline({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="flex items-center gap-2 text-xs text-gray-600">
            <span className="w-28 shrink-0">{label}</span>
            {children}
        </div>
    );
}

/** O resultado da simulação daquele domínio, do lado dos dials. */
function SimulationPanel({ result, onInspect }: {
    result?: BulkSimulateRow;
    onInspect: () => void;
}) {
    if (!result) {
        return (
            <div className="flex min-w-[190px] items-center justify-center rounded-lg border border-dashed border-gray-200 px-4 py-3 text-xs text-gray-400">
                não simulado
            </div>
        );
    }

    if (result.status === 'error') {
        return (
            <div className="min-w-[190px] max-w-xs rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                <X className="mb-1 h-3.5 w-3.5" />
                <span className="line-clamp-3" title={result.error ?? ''}>{result.error}</span>
            </div>
        );
    }

    const verdict = evaluateHealth(
        result.avg_steps_delivered, result.steps_requested,
        result.segments_found, result.segments_skipped,
    );

    return (
        <button
            type="button"
            onClick={onInspect}
            title={[verdict.summary, ...verdict.factors.map((f) => `• ${f.label}: ${f.detail}`)].join('\n')}
            className="min-w-[190px] space-y-1 rounded-lg border border-gray-200 px-3 py-2.5 text-left transition-colors hover:border-blue-300 hover:bg-blue-50/40"
        >
            <span
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${HEALTH_STYLE[verdict.level]}`}
            >
                {verdict.level === 'good' ? <Check className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                {HEALTH_LABEL[verdict.level]}
            </span>
            <div className="text-xs text-gray-600">
                {result.segments_found} segmentos · {result.segments_written} com regra
            </div>
            <div className="flex items-center gap-1 text-xs text-gray-500">
                média {result.avg_steps_delivered} de {result.steps_requested} steps
                <Eye className="h-3 w-3 text-blue-500" />
            </div>
        </button>
    );
}
